/**
 * useLoopThreads — read path for the inquiries and revision requests on one
 * or more catchball loops.
 *
 * Reads go straight to the tables (RLS: `is_cycle_participant(cycle_id_for_loop(loop_id))`).
 * Neither table is in the `supabase_realtime` publication (checked on the live
 * DB 2026-09-21 — only catchball_loops is), so there is no channel to
 * subscribe to. Freshness comes from: refetch on window focus / tab becoming
 * visible, an optional poll, and an explicit refetch after every write. That
 * is what makes a question opened on iOS show up here.
 *
 * No optimistic state: rows only ever come from the server.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTypedSupabaseClient } from '../../../../lib/typedSupabaseClient';
import type { LoopInquiry, LoopRevisionRequest } from './threadLogic';

export interface LoopThreadsState {
  inquiries: LoopInquiry[];
  revisionRequests: LoopRevisionRequest[];
  isLoading: boolean;
  /** Read failure, verbatim from the server. Null when the last read succeeded. */
  error: string | null;
  refetch: () => Promise<void>;
}

interface Options {
  /** Poll interval in ms. Omit / 0 for focus-only refresh (used by list chips). */
  pollMs?: number;
  /** Skip revision requests (the cycle-wide "waiting on you" read only needs inquiries). */
  inquiriesOnly?: boolean;
}

const INQUIRY_COLUMNS = 'id,loop_id,question,status,opened_by,opened_at,answer,answered_by,answered_at';
const REVISION_COLUMNS = 'id,loop_id,message,reason_code,status,requested_by,requested_at,closed_by,closed_at';

export function useLoopThreads(loopIds: string[], options: Options = {}): LoopThreadsState {
  const { pollMs = 0, inquiriesOnly = false } = options;
  // Stable key so callers can pass a fresh array each render.
  const key = useMemo(() => [...new Set(loopIds.filter(Boolean))].sort().join(','), [loopIds]);

  const [inquiries, setInquiries] = useState<LoopInquiry[]>([]);
  const [revisionRequests, setRevisionRequests] = useState<LoopRevisionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const refetch = useCallback(async () => {
    const ids = key ? key.split(',') : [];
    const seq = ++requestSeq.current;
    if (ids.length === 0) {
      setInquiries([]);
      setRevisionRequests([]);
      setError(null);
      return;
    }

    let supabase;
    try {
      supabase = getTypedSupabaseClient();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Database unavailable');
      return;
    }

    setIsLoading(true);
    const [inqRes, revRes] = await Promise.all([
      supabase
        .from('handoff_inquiries')
        .select(INQUIRY_COLUMNS)
        .in('loop_id', ids)
        .order('opened_at', { ascending: true }),
      inquiriesOnly
        ? Promise.resolve({ data: [], error: null })
        : supabase
            .from('revision_requests')
            .select(REVISION_COLUMNS)
            .in('loop_id', ids)
            .order('requested_at', { ascending: true }),
    ]);
    // A newer request superseded this one (loop changed, or a write refetched).
    if (seq !== requestSeq.current) return;
    setIsLoading(false);

    const failure = inqRes.error ?? revRes.error;
    if (failure) {
      // Keep the last good rows on screen; say the refresh failed.
      setError(failure.message);
      return;
    }
    setError(null);
    setInquiries(
      (inqRes.data ?? []).map((r) => ({
        id: r.id,
        loopId: r.loop_id,
        question: r.question,
        status: r.status,
        openedBy: r.opened_by,
        openedAt: r.opened_at,
        answer: r.answer,
        answeredBy: r.answered_by,
        answeredAt: r.answered_at,
      })),
    );
    setRevisionRequests(
      ((revRes.data ?? []) as Array<{
        id: string;
        loop_id: string;
        message: string;
        reason_code: string | null;
        status: string;
        requested_by: string;
        requested_at: string;
        closed_by: string | null;
        closed_at: string | null;
      }>).map((r) => ({
        id: r.id,
        loopId: r.loop_id,
        message: r.message,
        reasonCode: r.reason_code,
        status: r.status,
        requestedBy: r.requested_by,
        requestedAt: r.requested_at,
        closedBy: r.closed_by,
        closedAt: r.closed_at,
      })),
    );
  }, [key, inquiriesOnly]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!key) return;
    const onFocus = () => void refetch();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const timer =
      pollMs > 0
        ? window.setInterval(() => {
            if (document.visibilityState === 'visible') void refetch();
          }, pollMs)
        : null;
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      if (timer !== null) window.clearInterval(timer);
    };
  }, [key, pollMs, refetch]);

  return { inquiries, revisionRequests, isLoading, error, refetch };
}
